<template>
    <div v-if="Object.keys(registered_devices).length" class="patient-cards">
        <div class="beamer-toggler-container"
            v-for="dev in Object.keys(registered_devices).filter((dev) => registered_devices[dev].showing !== 'MTB Setup')">
            <span style="text-align: center; font-weight: 600">{{ registered_devices[dev].id }}</span>
            <div class="beamer-toggler">
                <span class="arrow grey-border">
                    <v-icon size="5rem" icon="mdi-menu-left" color="var(--default-blue)"
                        @click="onScreenPrev(registered_devices[dev])"></v-icon>
                </span>
                <span class="arrow grey-border">
                    <v-icon size="5rem" icon="mdi-menu-right" color="var(--default-blue)"
                        @click="onScreenNext(registered_devices[dev])"></v-icon>
                </span>
            </div>
        </div>
    </div>
    <div v-if="!Object.keys(registered_devices).length" class="carousel-sheet-container">
        <button id="ws-icon" @click="emit('toggle-websocket', { showing: 'MTB Setup' })">Connect</button>
        <p>Please turn on the WebSocket connection to start using the configuration functionality.</p>
    </div>
    <div v-if="Object.keys(registered_devices).length" class="mtb-setup-patients default-shadow">
        <div class="mtb-setup-current-patient">
            <p style="font-size:20px"><b>Current patient:</b> {{ current_patient.id }}</p>
            Please imagine some more patient data here :)
        </div>
        <div class="mtb-setup-next-prev-buttons">
            <v-btn @click="setPatientByIndex(-1)">Prev. Patient</v-btn>
            <v-btn @click="setPatientByIndex(1)">Next Patient</v-btn>
        </div>
    </div>
    <div class="patient-cards" v-if="Object.keys(registered_devices).length">
        <div class="patient-card default-shadow" v-for="patient in all_patients"
            :class="{ 'patient-card-selected': current_patient.id == patient.id }" @click="setPatientByID(patient.id)">
            <h2>{{ patient.id }}</h2>
            <p>&nbsp;{{ patient.biomarkers ? Object.keys(patient.biomarkers).length : 0 }} variants</p>
        </div>
    </div>
    <div class="mtb-setup-registered-devices">
        <div v-for="device of Object.keys(registered_devices)
        .filter((dev) => registered_devices[dev].id !== this_id && registered_devices[dev].showing !== 'MTB Setup')"
            class="single-device">
            <v-icon size="5rem" color="var(--default-blue)" icon="mdi-menu-left"
                @click="toPrevSlide(registered_devices[device])" />
            <MTB_Setup_Device :device_name="registered_devices[device].id"
                :currently_showing="registered_devices[device].showing"
                @show-slide="(slide) => showSlide(registered_devices[device], slide)" />
            <v-icon size="5rem" color="var(--default-blue)" icon="mdi-menu-right"
                @click="toNextSlide(registered_devices[device])" />
        </div>
    </div>
</template>

<script setup lang="js">
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import id_requests from '@/methods/id_requests';
import MTB_Setup_Device from '@/components/presentation_components/MTB_Setup_Device.vue';

const store = useStore();
const all_patients = computed(() => store.state.presentation_all_patients);
const selected_patients = computed(() => store.state.presentation_selected_patients);
const registered_devices = computed(() => store.state.presentation_registered_devices);
const current_patient = computed(() => store.state.presentation_current_patient);
const this_id = computed(() => store.state.presentation_device_id)
const participating_in_websocket = computed(() => props.websocket != null);
const slides = computed(() => store.state.presentation_slides);
const current_mtb = computed(() => store.state.current_mtb);

/**
 * @emit toggle-websocket - Toggles a websocket connection on or off (requires WS prop to be passed).
 */
const emit = defineEmits(['toggle-websocket']);

/**
 * @prop websocket - A websocket that can be passed down from App.vue. If no websocket connection exists, it is null.
 */
const props = defineProps({
    websocket: WebSocket | null,
})

/**
 * The following steps are performed if the MTB Config View is opened:
 * 1. Patient Data for the current MTB is loaded
 * 2. If no patient is selected, the first one is picked.
 */
onMounted(async () => {

    await id_requests.getMTBData(current_mtb.value);
    store.dispatch("setIDList", store.state.mtb_data[current_mtb.value]);
    fetchPatients();

    //If there is no current patient, select the first one in the list
    if (current_patient.value == '') {
        if (Object.keys(selected_patients.value).length > 0) store.dispatch('setPresentationPatient', selected_patients.value[Object.keys(selected_patients.value)[0]]);
        else store.dispatch('setPresentationPatient', all_patients.value[Object.keys(all_patients.value)[0]]);
    }

    if (current_patient.value.id && current_patient.value.id !== '') setup_selected_patient.value = current_patient.value.id;
})

/**
 * Loads patient data (for the current MTB) from store and filters them.
 * Then saves it in a dedicated presentation_all_patients variable.
 */
const fetchPatients = () => {
    var all_patients_raw = store.getters.idList;
    if (all_patients_raw !== undefined) {
        Object.keys(all_patients_raw).map(key => {
            delete all_patients_raw[key]['id'];
            all_patients_raw[key]['id'] = String(key);
        })
        store.dispatch('setPresentationAllPatients', all_patients_raw);
    }
}

/**
 * Tells a specific device (targeted by their ID) to show a specific slide.
 * @param {Object} device - Device Object
 * @param {String} slide - Slide number (as string, e.g. "0")
 */
const showSlide = (device, slide) => {
    props.websocket?.send(JSON.stringify({ codeword: 'show', slide: slide, device: device.id }));
}

/**
 * Tells a specific device (targeted by their ID) to go back one slide.
 * @param {Object} device - Device Object
 */
const toPrevSlide = (device) => {
    let index = store.getters.presentation_getpageindex(device.showing);
    showSlide(device.id, index == 0 ? Object.keys(slides.value).length - 1 : index - 1);
}

/**
 * Tells a specific device (targeted by their ID) to go forward one slide.
 * @param {Object} device - Device Object
 */
const toNextSlide = (device) => {
    let index = store.getters.presentation_getpageindex(device.showing);
    showSlide(device.id, index + 1 == Object.keys(slides.value).length ? 0 : index + 1);
}

/**
 * Tells a specific device (targeted by their ID) to go forward one table page or scroll downwards.
 * @param {Object} device - Device Object
 */
const onScreenNext = (device) => {
    props.websocket?.send(JSON.stringify({ codeword: 'next', device: device.id }));
}

/**
 * Tells a specific device (targeted by their ID) to go backwards one table page or scroll upwards.
 * @param {Object} device - Device Object
 */
const onScreenPrev = (device) => {
    props.websocket?.send(JSON.stringify({ codeword: 'prev', device: device.id }));
}

/**
 * Sets the current patient based on a starting position (patient_index) and an added or subtracted offset.
 * This means either a hard index can be set (offset 0, patient_index n) or the lists can be shuffled (offset -1/+1)
 * Index will either be applied to the all_patients list or the selected_patients list if the app is in selected mode.
 * @param {number} offset - Will be added to the given patient_index (which, by default, will be the current patient's index)
 * @param {object} patient_list - Patient list to use
 * @param {number} patient_index - Starting point in the list, optional (otherwise currently selected patient's index is used)
 * @param {boolean} send - Dictates whether to pass on the patient to other clients
 */
const setPatientByIndex = (offset, patient_index, send = true) => {
    store.dispatch('setPresentationPatientByIndex',
        { offset: offset, patient_index: patient_index });
    if (participating_in_websocket.value && send) {
        props?.websocket?.send(JSON.stringify({ codeword: 'adapt-current-patient-id', id: current_patient.value?.id }));
    }
}

/**
 * Sets the current patient based on the provided ID.
 * @param {string} patient_id - ID of the patient to set as current patient
 * @param {object} patient_list - Patient list to use
 * @param {boolean} send - Dictates whether to pass on the patient to other clients
 */
const setPatientByID = (patient_id, send = true) => {
    store.dispatch('setPresentationPatientByID',
        { patient_id: patient_id, });
    if (participating_in_websocket.value && send) {
        props?.websocket?.send(JSON.stringify({ codeword: 'adapt-current-patient-id', id: current_patient.value?.id }));
    }
}

/**
 * If the selected patient changes, this also has to be reflected in the configuration view.
 */
watch(() => current_patient.value, (cp) => {
    setup_selected_patient.value = cp.id;
})

const setup_selected_patient = ref('Please select a patient');
</script>

<style>
.beamer-toggler-container {
    min-height: 6rem;
    width: 50%;
    margin-top: 2%;
    text-align: center;
}

.beamer-toggler {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    color: #3d5afe;
}

#ws-icon {
    font-size: 1.125rem;
    width: fit-content;
    height: fit-content;
    min-height: 3rem;
    min-width: 4rem;
    padding: 8px;
    margin: 16px auto;
    border: var(--default-blue) 2px solid;
}

.patient-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    grid-auto-flow: row;
    width: 70%;
    margin: auto;
    align-items: center;
    justify-items: center;
}

.patient-card {
    width: 100%;
    height: fit-content;
    border: 2px solid var(--dark-grey);
    border-radius: 2px;
    padding: 4px 0 0;
    background-color: var(--light-grey);
    cursor: pointer;
}

.patient-card-selected {
    border: 2px solid var(--default-blue);
    background-color: var(--light-blue);
    cursor: default;
}

.grey-border {
    border: 2px solid #e3e3e3;
    border-radius: 10px;
    background-color: #f6f6f6;
}


.mtb-setup-registered-devices {
    display: grid;
    grid-auto-flow: row;
    grid-template-columns: repeat(1, minmax(100%, 1fr));
    margin: auto;
    margin-top: 2%;
    justify-items: center;
    align-items: center;
    width: 90%;
    overflow-y: scroll;
}

.single-device {
    display: grid;
    grid-template-columns: 1fr 18fr 1fr;
    grid-auto-flow: column;
    width: inherit;
    align-items: center;
}

.mtb-setup-patients {
    height: fit-content;
    max-height: 20rem;
    min-height: 10rem;
    display: grid;
    margin: 2% auto 2%;
    width: 85%;
    grid-template-columns: 4fr 2.5fr;
    background-color: var(--light-grey);
    align-items: center;
}

.mtb-setup-current-patient {
    width: 100%;
    height: 95%;
}

.mtb-setup-patient-selection {
    height: fit-content;
    max-height: 6rem;
    margin: 4px;
    background-color: var(--white);
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: row;
    row-gap: auto;
    align-items: center;
    justify-items: center;
}

.mtb-setup-patient-buttons {
    margin: 4px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 3fr;
    grid-auto-flow: row;
    row-gap: 2ch;
    align-items: center;
    justify-items: center;
}

.mtb-setup-next-prev-buttons {
    width: 100%;
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    flex-basis: 100px;
    flex-direction: row;
    justify-content: center;
    align-content: center;
    gap: 10px;
}

.mtb-setup-next-prev-buttons .v-btn {
    height: 60% !important;
}

.mtb-setup-patient-buttons .v-btn {
    width: 100% !important;
    font-size: 0.75rem;
    max-width: 150px !important;
    margin-right: 4px !important;
    height: 3rem !important;
}

.mtb-setup-current-patient {
    margin: 8px;
    border: 2px solid var(--light-grey);
    background-color: white;
    border-radius: 2px;
}

.arrow {
    font-size: 50%;
    text-align: center;
    cursor: pointer;
    -webkit-user-select: none;
    /* Safari */
    -moz-user-select: none;
    /* Firefox */
    -ms-user-select: none;
    /* IE10+/Edge */
    user-select: none;
    /* Standard */

}

.arrow:hover .v-icon {
    color: var((--light-blue)) !important;
}

.arrow:active {
    animation: bgfade 2s;
}

@keyframes bgfade {
    from {
        background-color: var(--light-grey);
    }

    to {
        background-color: #f6f6f6;
    }
}

@media (max-width: 1366px) {

    .mtb-setup-patients {
        font-size: 0.8rem !important;
    }

    .mtb-setup-registered-devices {
        max-height: 20rem;
    }


}

@media (max-width: 900px) {

    .mtb-setup-patients {
        font-size: 0.7rem !important;
    }

    .mtb-setup-registered-devices {
        max-height: 15rem;
    }

    .patient-cards {
        grid-template-columns: 1fr;
    }

    .patient-card {
        font-size: 0.9rem;
        max-height: 4rem;
    }

    .patient-card h2 {
        font-size: 1.125rem;
    }

    .mtb-setup-next-prev-buttons .v-btn {
        height: 40% !important;
    }

}
</style>

<style scoped>
#ws-icon:hover {
    scale: 1.1 1.1;
    transition-property: transform scale;
    transition: ease-in-out;
    cursor: pointer;
}

select {
    background-color: white;
    width: 100%;
    height: fit-content;
    padding: 8px 4px;
    -moz-appearance: menulist;
    border: 2px solid var(--light-grey);
    border-radius: 2px;
}
</style>